import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DisButton from '../components/design-system/DisButton';
import DisCard from '../components/design-system/DisCard';

const AdminPushSection = () => {
  const { api } = useAuth();
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [sending, setSending] = useState(false);
  const [pushForm, setPushForm] = useState({ title: '', body: '', target: 'all' });
  const [togglingRule, setTogglingRule] = useState(null);

  useEffect(() => {
    loadRules();
    loadLogs();
  }, []);

  const loadRules = async () => {
    try {
      const res = await api.get('/admin/push/rules');
      setRules(res.data.rules || []);
    } catch (e) { console.error('Erreur rules:', e); }
  };

  const loadLogs = async () => {
    try {
      const res = await api.get('/admin/push/logs?limit=30');
      setLogs(res.data.logs || []);
    } catch (e) { console.error('Erreur logs:', e); }
  };

  const toggleRule = async (ruleId) => {
    setTogglingRule(ruleId);
    try {
      await api.put(`/admin/push/rules/${ruleId}`);
      await loadRules();
    } catch (e) { console.error('Erreur toggle:', e); }
    setTogglingRule(null);
  };

  const handleSendPush = async () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) return;
    setSending(true);
    try {
      await api.post('/admin/push/send', pushForm);
      alert('Notification envoyée !');
      setPushForm({ title: '', body: '', target: 'all' });
      await loadLogs();
    } catch (e) {
      console.error('Erreur envoi push:', e);
      alert('Erreur lors de l\'envoi');
    } finally { setSending(false); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const categoryColors = {
    onboarding: 'bg-blue-100 text-blue-700',
    weekly: 'bg-green-100 text-green-700',
    retention: 'bg-orange-100 text-orange-700',
    event: 'bg-purple-100 text-purple-700',
  };

  const targetLabels = {
    all: 'Tous les utilisateurs',
    free: 'Gratuits uniquement',
    premium: 'Premium uniquement',
    explorer: 'Explorateur',
    family: 'Famille',
  };

  return (
    <DisCard className="p-6 mb-6" data-testid="push-section">
      <h2 className="text-xl font-bold text-gray-900 mb-4">🔔 Notifications Push</h2>

      {/* Envoi manuel */}
      <div className="bg-gray-50 rounded-xl p-4 mb-4" data-testid="push-send-form">
        <h3 className="text-sm font-bold text-gray-700 mb-3">Envoi manuel</h3>
        <div className="space-y-2">
          <input
            type="text"
            value={pushForm.title}
            onChange={(e) => setPushForm(p => ({...p, title: e.target.value}))}
            placeholder="Titre de la notification"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
            data-testid="push-title-input"
          />
          <textarea
            value={pushForm.body}
            onChange={(e) => setPushForm(p => ({...p, body: e.target.value}))}
            placeholder="Corps du message..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 resize-none"
            data-testid="push-body-input"
          />
          <div className="flex gap-2 items-center">
            <select
              value={pushForm.target}
              onChange={(e) => setPushForm(p => ({...p, target: e.target.value}))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1"
              data-testid="push-target-select"
            >
              {Object.entries(targetLabels).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <DisButton
              onClick={handleSendPush}
              disabled={sending || !pushForm.title.trim() || !pushForm.body.trim()}
              className="bg-teal-500 hover:bg-teal-600 text-white"
              data-testid="push-send-btn"
            >
              {sending ? 'Envoi...' : 'Envoyer'}
            </DisButton>
          </div>
        </div>
      </div>

      {/* Règles automatiques */}
      <div className="mb-4" data-testid="push-rules">
        <h3 className="text-sm font-bold text-gray-700 mb-2">Règles automatiques</h3>
        <div className="space-y-2">
          {rules.map(rule => (
            <div key={rule.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2" data-testid={`push-rule-${rule.id}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[rule.category] || 'bg-gray-100'}`}>
                  {rule.category}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{rule.name}</p>
                  <p className="text-xs text-gray-500 truncate">{rule.description}</p>
                </div>
              </div>
              <button
                onClick={() => toggleRule(rule.id)}
                disabled={togglingRule === rule.id}
                className={`ml-2 w-10 h-5 rounded-full transition-colors relative shrink-0 ${rule.active ? 'bg-teal-500' : 'bg-gray-300'}`}
                data-testid={`push-rule-toggle-${rule.id}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${rule.active ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Historique des notifications */}
      <div data-testid="push-logs">
        <button
          onClick={() => setShowLogs(!showLogs)}
          className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1 hover:text-teal-600"
          data-testid="push-logs-toggle"
        >
          Historique ({logs.length})
          <span className="text-xs">{showLogs ? '▲' : '▼'}</span>
        </button>
        {showLogs && (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {logs.length === 0 && (
              <p className="text-gray-400 text-xs text-center py-2">Aucune notification envoyée</p>
            )}
            {logs.map((log, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs bg-white border border-gray-100 rounded-lg px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${log.type === 'auto' ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                  {log.type === 'auto' ? 'Auto' : 'Manuel'}
                </span>
                <span className="text-gray-400 shrink-0">{formatDate(log.sent_at)}</span>
                <span className="text-gray-700 truncate flex-1" title={log.body}>
                  <strong>{log.title}</strong> — {log.body}
                </span>
                <span className={`shrink-0 ${log.status === 'sent' ? 'text-green-500' : 'text-red-500'}`}>
                  {log.status === 'sent' ? '✓' : '✗'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DisCard>
  );
};

export default AdminPushSection;
